"use client";

import BrandLogo from "@/components/layout/BrandLogo/BrandLogo";
import VendorsSelect from "@/components/vendors/VendorsSelect";
import { persistor, RootState } from "@/store";
import { logout } from "@/store/auth/authSlice";
import { clearVendors, setSelectedVendor } from "@/store/vendors/vendorsSlice";
import {
  Box,
  Collapse,
  Divider,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Popover,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  Archive,
  ChevronDown,
  ChevronRight,
  FileText,
  Handshake,
  Home,
  LayoutGrid,
  MapPin,
  Package,
  PackageCheck,
  Settings,
  ShoppingCart,
  Ticket,
  User,
  Warehouse,
} from "lucide-react";
import { usePathname, useRouter } from "next/navigation";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import ProfileMenu from "../ProfileMenu/ProfileMenu";
import * as classes from "./Sidebar.styles";

const drawerWidth = 240;
interface UserData {
  email: string;
  username?: string;
}

const baseMenuItems: Array<{
  key: string;
  icon?: React.ElementType;
  label: string;
  path?: string;
  type?: "section";
  children?: Array<{
    key: string;
    icon?: React.ElementType;
    label: string;
    path: string;
  }>;
}> = [
  {
    key: "dashboard",
    icon: Home,
    label: "Dashboard",
    path: "/",
  },
  {
    key: "apps-pages-section",
    type: "section",
    label: "APPS & PAGES",
  },
  { key: "orderreport", icon: ShoppingCart, label: "Order Report", path: "/report/order" },
  { key: "customerExport", icon: FileText, label: "Customer Export", path: "/reports/vendor-customers" },
  { key: "orders", icon: ShoppingCart, label: "Orders", path: "/orders" },
  {
    key: "settings",
    icon: Settings,
    label: "Setting",
    children: [
      { key: "products", icon: Package, label: "Products", path: "/products" },
      { key: "homeslider", icon: Package, label: "Home Slides", path: "/home-slides" },
      { key: "fulfilmentProfiles", icon: MapPin, label: "Fulfilment profiles", path: "/fulfilment-profiles" },
      { key: "categories", icon: LayoutGrid, label: "Categories", path: "/categories" },
      { key: "partners", icon: Handshake, label: "Partners", path: "/partners" },
      { key: "vendors", icon: Handshake, label: "Vendors", path: "/vendors" },
      { key: "customers", icon: User, label: "Customers", path: "/customers" },
      { key: "coupons", icon: Ticket, label: "Coupons", path: "/coupons" },
    ],
  },
  {
    key: "inventory",
    icon: Warehouse,
    label: "Inventory",
    children: [
      { key: "goodsReceipt", icon: PackageCheck, label: "GRN", path: "/goods-receipt" },
      { key: "stockoverview", icon: Archive, label: "Daily Stock", path: "/daily-stock" },
      { key: "inventoryMaster", icon: Package, label: "Adjust Stock", path: "/adjust-stock" },
      { key: "inventoryReport", icon: FileText, label: "Stock Report", path: "/inventory-report" },
    ],
  },
];

const isPathActive = (pathname: string, itemPath?: string) => {
  if (!itemPath || !pathname) return false;
  if (pathname === itemPath) return true;
  if (itemPath === "/") return false;

  if (pathname.startsWith(`${itemPath}/`)) {
    // if (itemPath === "/stock" && pathname.startsWith("/stock/adjust")) {
    //   return false;
    // }
    return true;
  }
  return false;
};

export default function Sidebar({
  mobileOpen,
  drawerOpen,
  handleDrawerToggle,
  openMenuKey,
  setOpenMenuKey,
}: {
  mobileOpen: boolean;
  drawerOpen: boolean;
  handleDrawerToggle: () => void;
  openMenuKey: string | null;
  setOpenMenuKey: React.Dispatch<React.SetStateAction<string | null>>;
}) {
  const dispatch = useDispatch();
  const pathname = usePathname();
  const router = useRouter();
  const theme = useTheme();
  const selectedVendorId = useSelector((s: RootState) => s.vendors?.selectedVendorId ?? null);
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const accessMap = useSelector((state: RootState) => state.auth.accessMap);
  const userData = useSelector((state: RootState) => state.auth.user) as UserData;
  const userType = useSelector((state: RootState) => state.auth.user?.user_type || "");
  const [popoverAnchor, setPopoverAnchor] = useState<{
    element: HTMLElement;
    children: (typeof baseMenuItems)[0]["children"];
  } | null>(null);
  const [closeTimeout, setCloseTimeout] = useState<NodeJS.Timeout | null>(null);
  useEffect(() => {
    const savedOpenMenuKey = localStorage.getItem("sidebar-open-menu");
    if (savedOpenMenuKey) {
      setOpenMenuKey(savedOpenMenuKey);
    }
  }, [setOpenMenuKey]);

  useEffect(() => {
    if (openMenuKey) {
      localStorage.setItem("sidebar-open-menu", openMenuKey);
    } else {
      localStorage.removeItem("sidebar-open-menu");
    }
  }, [openMenuKey]);

  const menuItems = baseMenuItems
    .map((item) => {
      if (item.children && item.children.length > 0) {
        const filteredChildren = item.children.filter(
          (child) => (accessMap?.nav as Record<string, boolean | undefined>)?.[child.key],
        );
        return { ...item, children: filteredChildren };
      }
      return item;
    })
    .filter((item) => {
      if (item.type === "section") return true;

      const hasAccess = (accessMap?.nav as Record<string, boolean | undefined>)?.[item.key];
      const hasVisibleChildren = !!item.children && item.children.length > 0;

      return hasAccess || hasVisibleChildren;
    });
  const handleLogout = async () => {
    dispatch(logout());
    dispatch(clearVendors());
    dispatch(setSelectedVendor(null));
    await persistor.purge();
    router.replace("/login");
  };

  const handlePopoverOpen = (
    event: React.MouseEvent<HTMLElement>,
    children?: (typeof baseMenuItems)[0]["children"],
  ) => {
    if (!drawerOpen && children && children.length > 0) {
      if (closeTimeout) {
        clearTimeout(closeTimeout);
        setCloseTimeout(null);
      }
      setPopoverAnchor({ element: event.currentTarget, children });
    }
  };

  const handlePopoverClose = () => {
    const timeout = setTimeout(() => setPopoverAnchor(null), 150);
    setCloseTimeout(timeout);
  };

  const drawerContent = (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" }}>
      <Box sx={{ display: "flex", alignItems: "center", height: 64 }}>
        <BrandLogo open={drawerOpen} onClose={handleDrawerToggle} isSmallScreen={isMobile} />
      </Box>

      <Divider />
      {/* pr: drawerOpen ? 2 : 1, py: 2 */}
      <Box sx={{ ...classes.overflowSize, flex: 1, overflowY: "auto", overflowX: "hidden", p: 2 }}>
        <List>
          {menuItems.map((item) => {
            if (item.type == "section") {
              if (!drawerOpen) return null;
              return (
                <Box key={item.key} sx={{ my: 2, display: { xs: "none", sm: "flex" } }}>
                  <Box sx={{ display: "flex", alignItems: "center", width: "100%" }}>
                    <Box sx={{ flex: 0.5, height: 1.5, backgroundColor: "#2E263D1F" }} />
                    <Typography
                      variant="caption"
                      sx={{
                        fontFamily: "Inter",
                        fontWeight: 400,
                        fontSize: "13px",
                        lineHeight: "18px",
                        letterSpacing: "0.4px",
                        color: theme.palette.text.disabled,
                        mx: 2,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {item.label}
                    </Typography>
                    <Box sx={{ flex: 1, height: 1.5, backgroundColor: "#2E263D1F" }} />
                  </Box>
                </Box>
              );
            }

            const { key, icon: Icon, label, path, children } = item;
            const hasChildren = !!children?.length;
            const isOpen = openMenuKey === key;
            const isParentActive =
              isPathActive(pathname, path) || children?.some((child) => isPathActive(pathname, child.path));

            return (
              <Box key={key}>
                <Tooltip title={!drawerOpen ? label : ""} placement="right" arrow>
                  <ListItemButton
                    onMouseEnter={(e) => handlePopoverOpen(e, children)}
                    onMouseLeave={handlePopoverClose}
                    onClick={() => {
                      if (hasChildren) {
                        setOpenMenuKey((prev) => (prev === key ? null : key));
                      } else if (path) {
                        setOpenMenuKey(null);
                        router.push(path);
                        if (isMobile) handleDrawerToggle();
                      }
                    }}
                    selected={isParentActive}
                    sx={{
                      ...classes.mainListItemButton(!!isParentActive),
                      // ...(isParentActive && {
                      // background: gradients.primary,
                      // color: "#fff",
                      // "& .MuiListItemIcon-root": {
                      //   color: "#fff",
                      // },
                      // }),
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: drawerOpen ? 32 : "unset",
                        justifyContent: "center",
                        color: "text.secondary",
                      }}
                    >
                      {Icon && <Icon size={16} />}
                    </ListItemIcon>

                    {drawerOpen && (
                      <>
                        <ListItemText
                          primary={label}
                          slotProps={{
                            primary: {
                              sx: classes.subListItemText,
                            },
                          }}
                        />

                        {hasChildren && (isOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />)}
                      </>
                    )}
                  </ListItemButton>
                </Tooltip>

                {hasChildren && drawerOpen && (
                  <Collapse in={isOpen} timeout="auto" unmountOnExit>
                    <Box
                      sx={{
                        ml: 2.5,
                        pl: 2,
                        borderLeft: "2px solid",
                        borderColor: "primary.main",
                      }}
                    >
                      {children.map((child) => {
                        const isSelected = pathname === child.path;
                        return (
                          <Tooltip key={child.key} title={child.label} placement="right" arrow>
                            <ListItemButton
                              onClick={() => {
                                router.push(child.path);
                                if (isMobile) handleDrawerToggle();
                              }}
                              selected={isSelected}
                              sx={classes.subListItemButton(isSelected)}
                            >
                              {child.icon && (
                                <ListItemIcon sx={{ minWidth: 30 }}>
                                  <child.icon size={16} />
                                </ListItemIcon>
                              )}

                              <ListItemText
                                primary={child.label}
                                slotProps={{
                                  primary: {
                                    sx: classes.subListItemText,
                                  },
                                }}
                              />
                            </ListItemButton>
                          </Tooltip>
                        );
                      })}
                    </Box>
                  </Collapse>
                )}
              </Box>
            );
          })}
        </List>
      </Box>

      <Box sx={{ borderTop: "1px solid #E0E0E0", p: 1 }}>
        {drawerOpen && (
          <Box
            sx={{
              mb: 2,
              display: userType === "superadmin" ? { xs: "block", md: "none" } : "none",
            }}
          >
            <VendorsSelect
              label=""
              blurOnSelect={true}
              value={selectedVendorId}
              disableClear={true}
              // disable={userType == "admin" ? true : false}
              onChange={(a: string | number | null, b?: string | number | null) => {
                const v = b !== undefined ? b : a;
                dispatch(setSelectedVendor(v));
              }}
            />
          </Box>
        )}

        <ProfileMenu drawerOpen={drawerOpen} userData={userData} handleLogout={handleLogout} />
      </Box>
      <Popover
        open={Boolean(popoverAnchor)}
        anchorEl={popoverAnchor?.element}
        onClose={handlePopoverClose}
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        sx={{
          pointerEvents: "none",
          "& .MuiPopover-paper": {
            pointerEvents: "auto",
            ml: 1,
            boxShadow: theme.shadows[8],
          },
        }}
        disableRestoreFocus
      >
        <Paper
          onMouseEnter={() => {
            if (closeTimeout) {
              clearTimeout(closeTimeout);
              setCloseTimeout(null);
            }
          }}
          onMouseLeave={handlePopoverClose}
          sx={{
            minWidth: 200,
            maxWidth: 280,
            boxShadow: "none",
          }}
        >
          <List sx={{ py: 1, m: 1 }}>
            {popoverAnchor?.children?.map((child) => {
              const isSelected = isPathActive(pathname, child.path);
              return (
                <ListItemButton
                  key={child.key}
                  onClick={() => {
                    router.push(child.path);
                    handlePopoverClose();
                    if (isMobile) handleDrawerToggle();
                  }}
                  selected={isSelected}
                  sx={classes.subListItemButton(isSelected)}
                >
                  {child.icon && (
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <child.icon size={18} />
                    </ListItemIcon>
                  )}
                  <ListItemText
                    primary={child.label}
                    slotProps={{
                      primary: {
                        sx: {
                          fontSize: "14px",
                          fontWeight: isSelected ? 500 : 400,
                        },
                      },
                    }}
                  />
                </ListItemButton>
              );
            })}
          </List>
        </Paper>
      </Popover>
    </Box>
  );

  return (
    <Box component="nav" sx={classes.drawerBox(drawerOpen, drawerWidth)}>
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true,
          hideBackdrop: true,
        }}
        sx={classes.smallScreenDrawer(drawerWidth)}
      >
        {drawerContent}
      </Drawer>

      <Drawer variant="permanent" open sx={classes.largeScreenDrawer(drawerOpen, drawerWidth)}>
        {drawerContent}
      </Drawer>
    </Box>
  );
}
